import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSelectedDealer } from "components/DealersAndLocationsDropdown/hooks";
import { ReactTable } from "components/ReactTable";
import { DEALER_REPORT_COLUMNSS } from "models";
import { DealerUserReportsCustomHeader } from "modules/DealerReports/components/DealerUsersReport/components";
import "modules/DealerReports/components/DealerUsersReport/DealerUsersReport.scss";
import { useDealerThresholds } from "modules/DealerReports/hooks/useDealerThresholds";

import type { ParsedReportData } from "modules/DealerReports/util/parser";
import type { ChangeEvent } from "react";

enum THRESHOLD_COLORS {
  Min = "#FCD6D0",
  Max = "#DAEFDA",
  Default = "transparent"
}

enum THRESHOLD_VALUES {
  Min = "min",
  Max = "max"
}

type ThresholdRangeType = {
  [key: string]: { min: number | null; max: number | null };
};

const THRESHOLD_COLUMN_FIELDS = new Map<DEALER_REPORT_COLUMNSS, string>([
  [DEALER_REPORT_COLUMNSS.WOHandled, "wo_handled"],
  [DEALER_REPORT_COLUMNSS.CheckAanname, "check_aanname"],
  [DEALER_REPORT_COLUMNSS.CheckCommercial, "check_commercial"],
  [DEALER_REPORT_COLUMNSS.CheckMedia, "check_media"],
  [DEALER_REPORT_COLUMNSS.CheckCount, "check_count"],
  [DEALER_REPORT_COLUMNSS.StatusQuality, "status_quality"],
  [DEALER_REPORT_COLUMNSS.CCExtraServices, "cc_extra_services"]
]);

export type ReportRow = {
  [key: string]: any;
};

type DealerUsersReportTableProps = {
  data: ParsedReportData[];
};

const formatTableCell = (value: any, color: THRESHOLD_COLORS): React.ReactNode => {
  if (value === null || value === undefined) return "0";

  if (typeof value === "number")
    return (
      <span className="threshold-value-cell" style={{ backgroundColor: color }}>
        {value.toString().includes(".") ? Number(value.toFixed(2)) : value}
      </span>
    );
  return value;
};

const columnHelper = createColumnHelper<ReportRow>();

export const DealerUsersReportTable = ({ data }: DealerUsersReportTableProps) => {
  const { t } = useTranslation();

  const { selectedDealer } = useSelectedDealer();
  const thresholdsList = useDealerThresholds(Number(selectedDealer?.id));
  const [thresholdRanges, setThresholdRanges] = useState<ThresholdRangeType | null>(null);

  useEffect(() => {
    if (!thresholdsList.data) return;
    const defaultThresholdRanges: ThresholdRangeType = thresholdsList.data.reduce<ThresholdRangeType>((acc, threshold) => {
      const columnName = THRESHOLD_COLUMN_FIELDS.get(threshold.dealer_report_column);
      if (!columnName) return acc;
      acc[columnName] = {
        min: threshold.low_value ?? null,
        max: threshold.high_value ?? null
      };
      return acc;
    }, {});
    setThresholdRanges(defaultThresholdRanges);
  }, [thresholdsList.data]);

  const getThresholdColor = (value: number, key: string) => {
    if (!thresholdRanges) return THRESHOLD_COLORS.Default;

    const thresholdRange = key in thresholdRanges ? thresholdRanges[key] : null;
    if (thresholdRange?.min && thresholdRange.min >= value) return THRESHOLD_COLORS.Min;
    if (thresholdRange?.max && thresholdRange.max <= value) return THRESHOLD_COLORS.Max;

    return THRESHOLD_COLORS.Default;
  };

  const filteredColumns = Object.keys(data[0]).filter(key => key !== "__typename");

  const columns = filteredColumns.map(key =>
    columnHelper.accessor(key, {
      header: () => <strong>{t(`v8_${key.replace("user_", "")}`) || key}</strong>,
      cell: info => {
        const color = getThresholdColor(info.getValue(), key);
        return formatTableCell(info.getValue(), color);
      }
    })
  );

  const updateThresholdCache = (key: string, value: string, type: "min" | "max") => {
    if (!thresholdRanges) return;
    setThresholdRanges({
      ...thresholdRanges,
      [key]: {
        ...thresholdRanges[key],
        [type]: Math.max(Number(value), 0)
      }
    });
  };

  const thresholdInputChange = (e: ChangeEvent<HTMLInputElement>, thresholdType: THRESHOLD_VALUES, key: string) => {
    const inputValue = String(Math.max(Number(e.target.value), 0));
    e.target.value = inputValue;
    updateThresholdCache(key, inputValue, thresholdType);
  };

  const thresholdInputs = filteredColumns.map((key, i) => {
    if (i === 0)
      return (
        <th key={key} className="threshold-placeholder">
          {t("v8_threshold", "Threshold")}
        </th>
      );

    if (thresholdRanges && key in thresholdRanges) {
      const range = thresholdRanges[key as keyof typeof thresholdRanges];

      return (
        <th key={key}>
          <div className="thresholdrange-wrapper">
            <input
              className="min-threshold-input"
              type="number"
              min={0}
              defaultValue={range.min || 0}
              onChange={e => thresholdInputChange(e, THRESHOLD_VALUES.Min, key)}
            />
            <input
              className="max-threshold-input"
              type="number"
              min={0}
              defaultValue={range.max || 0}
              onChange={e => thresholdInputChange(e, THRESHOLD_VALUES.Max, key)}
            />
          </div>
        </th>
      );
    }

    return <th key={key}></th>;
  });

  return (
    <div className="DealerUsersReportTable">
      <div style={{ maxHeight: "600px", overflow: "auto" }}>
        <ReactTable
          data={data}
          columns={columns}
          customHeader={props => (
            <>
              <DealerUserReportsCustomHeader {...props} /> {Boolean(thresholdsList.data?.length) && <tr>{thresholdInputs}</tr>}
            </>
          )}
        />{" "}
      </div>
    </div>
  );
};
